import { X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

// Sort Icon Component (matching audience results)
const SortIcon = ({ column, currentSortColumn, sortDirection }) => {
	const isActive = currentSortColumn === column;

	return (
		<span className="sort-icon">
			<svg
				width="12"
				height="12"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<path
					d="M7 14l5-5 5 5"
					fill={isActive && sortDirection === "asc" ? "currentColor" : "none"}
					stroke={isActive && sortDirection === "asc" ? "currentColor" : "#ccc"}
					strokeWidth={isActive && sortDirection === "asc" ? 2 : 1}
				/>
				<path
					d="M7 10l5 5 5-5"
					fill={isActive && sortDirection === "desc" ? "currentColor" : "none"}
					stroke={
						isActive && sortDirection === "desc" ? "currentColor" : "#ccc"
					}
					strokeWidth={isActive && sortDirection === "desc" ? 2 : 1}
				/>
			</svg>
		</span>
	);
};

// Helper function to determine segment type
const getSegmentTypeHelper = (segment, categoryDataMap = null) => {
	const segmentId = segment.segmentVector?.segmentId;

	// First method: Check categoryData mapping by segment ID (most reliable)
	if (segmentId && categoryDataMap) {
		const categoryType = categoryDataMap[segmentId];
		if (categoryType && (categoryType === "OA" || categoryType === "OAA")) {
			return categoryType;
		}
	}

	// Second method: Try to get the category directly from the backend response
	const directCategory = segment.segmentVector?.segmentCategory;
	if (directCategory) {
		const upperCategory = directCategory.toUpperCase();
		if (upperCategory.includes("OA") && !upperCategory.includes("OAA")) {
			return "OA";
		} else if (upperCategory.includes("OAA")) {
			return "OAA";
		}
	}

	// Third method: Fallback to the original logic if direct category not available
	const category = segment.segmentVector?.category;
	const subCategory = segment.segmentVector?.sub_category;

	if (category === "predefined") {
		if (
			[
				"third_party_data",
				"past_browsing_interests",
				"partner_audiences",
			].includes(subCategory)
		) {
			return "OA";
		} else if (subCategory === "bulk") {
			return "OAA";
		}
	}

	// Default to OAA if unable to determine
	return "OAA";
};

// Enhanced Set Details Modal with actual API call
export const SetDetailsModal = ({
	setKey,
	setData,
	segmentsData,
	demographicProbabilities,
	isOpen,
	activeTab,
	onClose,
	onSegmentSelect,
	selectedSegments = [],
	categoryDataMap = null,
}) => {
	const [sortBy, setSortBy] = useState("compositeScore");
	const [sortOrder, setSortOrder] = useState("desc");
	const [modalActiveTab, setModalActiveTab] = useState("overview");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [setDetails, setSetDetails] = useState(null);
	const [orfMetrics, setOrfMetrics] = useState(null);

	// Get segments from the specific search term only - FIXED VERSION
	const setSegments = useMemo(() => {
		if (!setData?.segmentIds || !segmentsData || !activeTab) {
			console.log("Missing setData.segmentIds, segmentsData, or activeTab");
			return [];
		}

		const segmentIds = setData.segmentIds.split(",").map((id) => id.trim());
		console.log("Looking for segment IDs:", segmentIds, "in tab:", activeTab);

		// Get data only from the active tab to avoid duplicates
		const activeTabData = segmentsData[activeTab];
		if (
			!activeTabData ||
			!activeTabData.data ||
			!Array.isArray(activeTabData.data)
		) {
			console.log("No data found for active tab:", activeTab);
			return [];
		}

		// Filter segments from the active tab only
		const matchedSegments = activeTabData.data.filter((segment) => {
			const segmentId = segment.segmentVector?.segmentId;
			const isMatch = segmentIds.includes(segmentId);

			if (isMatch) {
				console.log(
					`Found matching segment in ${activeTab}: ${segment.segmentVector?.segmentName} (${segmentId})`,
				);
			}

			return isMatch;
		});

		console.log(
			`Final result: ${matchedSegments.length} segments from ${activeTab}`,
		);
		return matchedSegments;
	}, [setData, segmentsData, setKey, activeTab]);

	// Enhanced aggregated metrics calculation - now includes ORF from API and demographic multipliers
	const aggregatedMetrics = useMemo(() => {
		if (!setSegments || setSegments.length === 0) {
			return {
				// Key Performance Indicators
				totalReach: 0,
				totalImpressions: 0,
				numberOfAudiences: 0,
				totalSpend: 0,
				reportMonths: 0,
				avgCompositeScore: 0,
				avgSearchScore: 0,

				// Engagement Metrics
				totalClicks: 0,
				totalWeightedImpressionsWithClickMacro: 0,
				avgClickRate: 0,
				avgViewability: 0,
				totalVideoCompletions: 0,

				// Revenue Metrics
				avgEcpm: 0,
				ecpmRange: { min: 0, max: 0 },
				avgSpendPerUser: 0,
				totalAdvertiserSpend: 0,

				// Scale Metrics
				totalRequests: 0,
				totalMatchedCount: 0,
				totalCookies: 0,
				totalFills: 0,

				// Device Distribution
				totalMobileDevices: 0,
				totalTabletDevices: 0,
				totalConnectedTvDevices: 0,
				totalDesktopDevices: 0,

				segmentCount: 0,

				// ORF Metrics from API
				orf: orfMetrics?.orf || 0,
				effectiveReach: orfMetrics?.effectiveReach || 0,
				standardDeviation: orfMetrics?.stdDev || 0,
			};
		}

		// Helper function to calculate demographic multiplier
		const calculateDemographicMultiplier = (demographicProbabilities) => {
			if (!demographicProbabilities) {
				console.log(
					"No demographic probabilities provided, using multiplier of 1",
				);
				return 1;
			}

			const ageProb = demographicProbabilities.age || 1;
			const locProb = demographicProbabilities.location || 1;
			const genderProb = demographicProbabilities.gender || 1;

			const multiplier = ageProb * locProb * genderProb;

			return multiplier;
		};

		// Calculate the demographic multiplier
		const demographicMultiplier = calculateDemographicMultiplier(
			demographicProbabilities,
		);

		// Get ORF value (default to 1 if not available to avoid zeroing out all values)
		const orfValue = orfMetrics?.orf || 1;

		// Combined multiplier for all metrics
		const combinedMultiplier = orfValue * demographicMultiplier;

		const metrics = setSegments.reduce(
			(acc, segment) => {
				const segmentMetrics = segment.segmentMetrics || {};
				const segmentVector = segment.segmentVector || {};

				// Apply multiplier to all numerical metrics
				// Key Performance Indicators
				acc.totalReach +=
					(segmentMetrics.calculatedReach || segmentMetrics.uniqueUsers || 0) *
					combinedMultiplier;
				acc.totalImpressions +=
					(segmentMetrics.weightedImpressions ||
						segmentMetrics.impressions ||
						0) * combinedMultiplier;
				acc.totalSpend +=
					(segmentMetrics.weightedSpendInUsd ||
						segmentMetrics.spendInUsd ||
						0) * combinedMultiplier;

				// Engagement Metrics
				acc.totalClicks +=
					(segmentMetrics.weightedClicks || 0) * combinedMultiplier;
				acc.totalWeightedImpressionsWithClickMacro +=
					(segmentMetrics.weightedImpressionsWithClickMacro || 0) *
					combinedMultiplier;
				acc.totalVideoCompletions +=
					(segmentMetrics.videoCompletions || 0) * combinedMultiplier;

				// For percentages, we still collect them without multiplier for averaging
				acc.viewabilities.push(segmentMetrics.weightedViewability || 0);

				// Revenue Metrics - multiply the absolute values, collect rates for averaging
				acc.ecpms.push(segmentMetrics.avgCpm || 0); // eCPM is a rate, don't multiply
				acc.spendPerUsers.push(segmentMetrics.avgSpendPerUniqueUser || 0); // But spend per user should be multiplied
				acc.totalAdvertiserSpend += segmentMetrics.advertiserSpend || 0;

				// Scale Metrics
				acc.totalRequests +=
					(segmentMetrics.requests || 0) * combinedMultiplier;
				acc.totalMatchedCount +=
					(segmentVector.sourceMatchedCount || 0) * combinedMultiplier;
				acc.totalCookies += (segmentMetrics.cookies || 0) * combinedMultiplier;
				acc.totalFills +=
					(segmentMetrics.weightedFills || 0) * combinedMultiplier;

				// Device Distribution
				acc.totalMobileDevices +=
					(segmentMetrics.mobileDevices || 0) * combinedMultiplier;
				acc.totalTabletDevices +=
					(segmentMetrics.tabletDevices || 0) * combinedMultiplier;
				acc.totalConnectedTvDevices +=
					(segmentMetrics.connectedTvDevices || 0) * combinedMultiplier;
				acc.totalDesktopDevices +=
					(segmentMetrics.desktopDevices || 0) * combinedMultiplier;

				// For averages - scores should not be multiplied as they are quality metrics
				acc.compositeScores.push(segment.compositeScore || 0);
				acc.searchScores.push(segment.searchScore || 0);

				return acc;
			},
			{
				totalReach: 0,
				totalImpressions: 0,
				totalSpend: 0,
				totalClicks: 0,
				totalWeightedImpressionsWithClickMacro: 0,
				totalVideoCompletions: 0,
				totalAdvertiserSpend: 0,
				totalRequests: 0,
				totalMatchedCount: 0,
				totalCookies: 0,
				totalFills: 0,
				totalMobileDevices: 0,
				totalTabletDevices: 0,
				totalConnectedTvDevices: 0,
				totalDesktopDevices: 0,
				compositeScores: [],
				searchScores: [],
				viewabilities: [],
				ecpms: [],
				spendPerUsers: [],
			},
		);

		// Calculate averages and derived metrics
		const segmentCount = setSegments.length;
		const avgCompositeScore =
			metrics.compositeScores.length > 0
				? metrics.compositeScores.reduce((a, b) => a + b, 0) /
					metrics.compositeScores.length
				: 0;
		const avgSearchScore =
			metrics.searchScores.length > 0
				? metrics.searchScores.reduce((a, b) => a + b, 0) /
					metrics.searchScores.length
				: 0;
		const avgClickRate =
			metrics.totalWeightedImpressionsWithClickMacro > 0
				? metrics.totalClicks / metrics.totalWeightedImpressionsWithClickMacro
				: 0;
		const avgViewability =
			metrics.viewabilities.length > 0
				? metrics.viewabilities.reduce((a, b) => a + b, 0) /
					metrics.viewabilities.length
				: 0;
		const avgEcpm =
			metrics.ecpms.length > 0
				? metrics.ecpms.reduce((a, b) => a + b, 0) / metrics.ecpms.length
				: 0;
		const avgSpendPerUser =
			metrics.spendPerUsers.length > 0
				? metrics.spendPerUsers.reduce((a, b) => a + b, 0) /
					metrics.spendPerUsers.length
				: 0;

		// eCPM Range - not multiplied as it's a rate
		const nonZeroEcpms = metrics.ecpms.filter((e) => e > 0);
		const ecpmRange =
			nonZeroEcpms.length > 0
				? { min: Math.min(...nonZeroEcpms), max: Math.max(...nonZeroEcpms) }
				: { min: 0, max: 0 };

		// Estimate report months (simplified calculation)
		const reportMonths = Math.min(
			12,
			Math.max(1, Math.ceil(segmentCount / 10)),
		);

		const finalMetrics = {
			// Key Performance Indicators
			totalReach: metrics.totalReach,
			totalImpressions: metrics.totalImpressions,
			numberOfAudiences: segmentCount,
			totalSpend: metrics.totalSpend,
			reportMonths,
			avgCompositeScore,
			avgSearchScore,

			// Engagement Metrics
			totalClicks: metrics.totalClicks,
			avgClickRate,
			avgViewability,
			totalVideoCompletions: metrics.totalVideoCompletions,

			// Revenue Metrics
			avgEcpm,
			ecpmRange,
			avgSpendPerUser,
			totalAdvertiserSpend: metrics.totalAdvertiserSpend,

			// Scale Metrics
			totalRequests: metrics.totalRequests,
			totalMatchedCount: metrics.totalMatchedCount,
			totalCookies: metrics.totalCookies,
			totalFills: metrics.totalFills,

			// Device Distribution
			totalMobileDevices: metrics.totalMobileDevices,
			totalTabletDevices: metrics.totalTabletDevices,
			totalConnectedTvDevices: metrics.totalConnectedTvDevices,
			totalDesktopDevices: metrics.totalDesktopDevices,

			// ORF Metrics from API (these are not multiplied as they are analysis results)
			orf: orfMetrics?.orf || 0,
			effectiveReach: (orfMetrics?.effectiveReach || 0) * demographicMultiplier, // Apply only demographic multiplier to effective reach
			standardDeviation: orfMetrics?.stdDev || 0,

			// Store multiplier info for debugging/display
			appliedMultipliers: {
				orfValue,
				demographicMultiplier,
				combinedMultiplier,
			},

			segmentCount,
		};

		return finalMetrics;
	}, [setSegments, orfMetrics, demographicProbabilities]);

	// Sort segments
	const sortedSegments = useMemo(() => {
		if (!setSegments || setSegments.length === 0) return [];

		return [...setSegments].sort((a, b) => {
			let aVal, bVal;

			switch (sortBy) {
				case "segmentName":
					aVal = a.segmentVector?.segmentName || "";
					bVal = b.segmentVector?.segmentName || "";
					break;
				case "audienceType":
					aVal = a.segmentVector?.audienceType || a.audienceType || "";
					bVal = b.segmentVector?.audienceType || b.audienceType || "";
					break;
				case "provider":
					aVal = a.segmentVector?.provider || a.provider || "";
					bVal = b.segmentVector?.provider || b.provider || "";
					break;
				case "category":
					aVal = getSegmentTypeHelper(a, categoryDataMap) || "ZZZ";
					bVal = getSegmentTypeHelper(b, categoryDataMap) || "ZZZ";
					break;
				case "uniqueUsers":
					aVal = a.segmentMetrics?.calculatedReach || 0;
					bVal = b.segmentMetrics?.calculatedReach || 0;
					break;
				case "uniqueUsersViaConnectedTv":
					aVal = a.segmentMetrics?.uniqueUsersViaConnectedTv || 0;
					bVal = b.segmentMetrics?.uniqueUsersViaConnectedTv || 0;
					break;
				case "mobileDevices":
					aVal = a.segmentMetrics?.mobileDevices || 0;
					bVal = b.segmentMetrics?.mobileDevices || 0;
					break;
				case "connectedTvDevices":
					aVal = a.segmentMetrics?.connectedTvDevices || 0;
					bVal = b.segmentMetrics?.connectedTvDevices || 0;
					break;
				case "tabletDevices":
					aVal = a.segmentMetrics?.tabletDevices || 0;
					bVal = b.segmentMetrics?.tabletDevices || 0;
					break;
				case "weightedImpressions":
					aVal = a.segmentMetrics?.weightedImpressions || 0;
					bVal = b.segmentMetrics?.weightedImpressions || 0;
					break;
				case "requests":
					aVal = a.segmentMetrics?.requests || 0;
					bVal = b.segmentMetrics?.requests || 0;
					break;
				case "activeUsersWithImpressions":
					aVal = a.segmentMetrics?.uniqueUsersWithImpressions || 0;
					bVal = b.segmentMetrics?.uniqueUsersWithImpressions || 0;
					break;
				case "spendInUsd":
					aVal = a.segmentMetrics?.weightedSpendInUsd || 0;
					bVal = b.segmentMetrics?.weightedSpendInUsd || 0;
					break;
				case "avgEcpm":
					aVal = a.segmentMetrics?.avgCpm || 0;
					bVal = b.segmentMetrics?.avgCpm || 0;
					break;
				case "compositeScore":
				default:
					aVal = a.compositeScore || 0;
					bVal = b.compositeScore || 0;
					break;
			}

			if (typeof aVal === "string") {
				return sortOrder === "asc"
					? aVal.localeCompare(bVal)
					: bVal.localeCompare(aVal);
			}

			return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
		});
	}, [setSegments, sortBy, sortOrder, categoryDataMap]);

	// Fixed API call handler for ORF metrics
	const fetchSetDetails = async (setKey, setData) => {
		if (!setData?.segmentIds) {
			console.warn("No segmentIds found in setData");
			return;
		}

		setLoading(true);
		setError(null);

		try {
			// Extract segment IDs from the set
			const segmentIds = setData.segmentIds.split(",").map((id) => id.trim());
			console.log("Making API call for segment IDs:", segmentIds);

			// Check if the segments API service is available
			if (!window.segmentsApiService) {
				throw new Error("Segments API service not available");
			}

			// Build segment reach map from available segment data
			const segmentReachMap = {};
			const allSegments = [];

			// Collect all segments from segmentsData to get reach values
			Object.entries(segmentsData).forEach(([key, searchTermData]) => {
				if (
					searchTermData &&
					searchTermData.data &&
					Array.isArray(searchTermData.data)
				) {
					allSegments.push(...searchTermData.data);
				}
			});

			// Map segment IDs to their reach values
			segmentIds.forEach((segmentId) => {
				const segment = allSegments.find(
					(s) => s.segmentVector?.segmentId === segmentId,
				);
				if (segment) {
					segmentReachMap[segmentId] =
						segment.segmentMetrics?.calculatedReach ||
						segment.segmentMetrics?.reach ||
						segment.segmentMetrics?.uniqueUsers ||
						1000000; // fallback default
				}
			});

			console.log("Segment reach map:", segmentReachMap);

			// Make the actual API call using the ORF endpoint
			const orfRequest = {
				segmentIds: segmentIds,
				segmentReachMap: segmentReachMap,
				operator: "UNION",
			};

			console.log("ORF API Request:", orfRequest);

			// Call the ORF API directly using the service
			const response =
				await window.segmentsApiService.calculateOrfForSegments(orfRequest);

			if (!response.success) {
				throw new Error(response.error || "Failed to fetch ORF metrics");
			}

			console.log("ORF API Response:", response.data);

			// Extract ORF from the correct nested response structure
			const responseBody = response.data;
			const reachTree = responseBody?.reachTree || {};
			const orfValue = reachTree?.orf || 0;

			console.log("Extracted ORF value:", orfValue);

			// Store the ORF metrics - only the ORF value since that's what you need
			const orfMetricsData = {
				orf: orfValue,
				effectiveReach: reachTree?.effectiveReach || 0,
				finalReach: responseBody?.finalReach || 0,
				operator: reachTree?.operator || orfRequest.operator,
				totalSegments: segmentIds.length,
				requestedSegments: segmentIds,
				lastUpdated: new Date().toISOString(),
			};

			setOrfMetrics(orfMetricsData);
			console.log("Setting ORF Metrics with value:", orfValue);

			// Store additional set details
			setSetDetails({
				orf: orfValue,
				effectiveReach: reachTree?.effectiveReach || 0,
				finalReach: responseBody?.finalReach || 0,
				operator: reachTree?.operator || orfRequest.operator,
				requestSegments: segmentIds,
				lastUpdated: new Date().toISOString(),
				additionalInfo: "ORF metrics loaded from API",
			});
		} catch (err) {
			console.error("Error fetching set details:", err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	// Reset to overview tab and fetch details when modal opens
	useEffect(() => {
		if (isOpen && setKey && setData) {
			console.log("🎭 Modal opened, fetching set details for:", setKey);
			setModalActiveTab("overview");

			// Clear previous state
			setOrfMetrics(null);
			setSetDetails(null);
			setError(null);

			// Make the API call
			fetchSetDetails(setKey, setData);
		}
	}, [isOpen, setKey, setData]);

	// Clear state when modal closes
	useEffect(() => {
		if (!isOpen) {
			setSetDetails(null);
			setOrfMetrics(null);
			setError(null);
			setLoading(false);
		}
	}, [isOpen]);

	if (!isOpen) return null;

	const formatNumber = (num) => {
		if (!num) return "0";
		if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
		if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
		if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
		return num.toLocaleString();
	};

	const formatPercentage = (num) => {
		if (!num) return "0.00%";
		return `${num.toFixed(2)}%`;
	};

	const formatCurrency = (num) => {
		if (!num) return "$0.00";
		return `$${formatNumber(num)}`;
	};

	const getDisplayName = (key) => {
		const displayMap = {
			Perfomance: "Performance Set",
			Performance: "Performance Set",
			Technology: "Technology Set",
			Automotive: "Automotive Set",
			Travel: "Travel Set",
			Fashion: "Fashion Set",
		};

		// Handle prefixed set keys (e.g., "sports_Perfomance")
		if (key.includes("_")) {
			const parts = key.split("_");
			if (parts.length === 2) {
				const [searchTerm, setType] = parts;
				const capitalizedSearchTerm =
					searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1);
				const setTypeDisplay =
					setType === "Perfomance" ? "Performance" : setType;
				return `${capitalizedSearchTerm} ${setTypeDisplay} Set`;
			}
		}

		return displayMap[key] || key.replace(/([A-Z])/g, " $1").trim();
	};

	const getCompositeScoreClass = (score) => {
		if (score >= 80) return "excellent";
		if (score >= 70) return "great";
		if (score >= 60) return "acceptable";
		if (score >= 50) return "poor";
		return "irrelevant";
	};

	const handleSort = (column) => {
		if (sortBy === column) {
			setSortOrder(sortOrder === "asc" ? "desc" : "asc");
		} else {
			setSortBy(column);
			setSortOrder("desc");
		}
	};

	// Helper function to get ORF efficiency level
	const getOrfEfficiency = (orf) => {
		if (orf > 0.8)
			return { level: "Excellent", color: "#28a745", bgColor: "#d4edda" };
		if (orf > 0.6)
			return { level: "Good", color: "#17a2b8", bgColor: "#d1ecf1" };
		if (orf > 0.4)
			return { level: "Fair", color: "#ffc107", bgColor: "#fff3cd" };
		return { level: "Poor", color: "#dc3545", bgColor: "#f8d7da" };
	};

	const orfEfficiency = getOrfEfficiency(aggregatedMetrics.orf);

	return (
		<>
			<div className="modal-overlay" onClick={onClose}>
				<div
					className="modal-content set-modal-enhanced"
					onClick={(e) => e.stopPropagation()}
				>
					<div className="modal-header">
						<div className="header-content">
							<h3 className="modal-main-title">{getDisplayName(setKey)}</h3>
							<button onClick={onClose} className="modal-close">
								<X className="w-5 h-5" />
							</button>
						</div>
					</div>

					{/* Tab Navigation */}
					<div className="modal-tabs">
						<button
							className={`modal-tab ${modalActiveTab === "overview" ? "active" : ""}`}
							onClick={() => setModalActiveTab("overview")}
						>
							<span className="tab-icon">📊</span>
							Performance & Scale
							{loading && modalActiveTab === "overview" && (
								<span className="tab-loading">...</span>
							)}
						</button>
						<button
							className={`modal-tab ${modalActiveTab === "segments" ? "active" : ""}`}
							onClick={() => setModalActiveTab("segments")}
						>
							<span className="tab-icon">🎯</span>
							Segment Details
						</button>
					</div>

					<div className="modal-body">
						{/* Error State */}
						{error && (
							<div className="error-section">
								<div className="error-icon">⚠️</div>
								<h3>Error Loading Set Details</h3>
								<p>{error}</p>
								<button
									onClick={() => fetchSetDetails(setKey, setData)}
									className="retry-button"
								>
									Retry
								</button>
							</div>
						)}

						{/* Enhanced Overview Tab Content */}
						{modalActiveTab === "overview" && !error && (
							<div className="tab-content">
								{/* Key Performance Indicators */}
								<div className="kpi-section">
									<h4 className="section-title">Key Performance Indicators</h4>
									<div className="kpi-grid">
										<div className="kpi-card">
											<div className="kpi-value">
												{formatNumber(aggregatedMetrics.totalReach)}
											</div>
											<div className="kpi-label">REACH</div>
										</div>
										<div className="kpi-card">
											<div className="kpi-value">
												{formatNumber(aggregatedMetrics.totalImpressions)}
											</div>
											<div className="kpi-label">IMPRESSIONS</div>
										</div>
										<div className="kpi-card">
											<div className="kpi-value">
												{aggregatedMetrics.numberOfAudiences}
											</div>
											<div className="kpi-label">NO. OF SEGMENTS</div>
										</div>
									</div>
								</div>

								{/* Performance Metrics Grid */}
								<div className="performance-section">
									<h4 className="section-title">Performance Metrics</h4>
									<div className="metrics-grid">
										{/* Engagement Group */}
										<div className="metric-group">
											<h5 className="group-title">ENGAGEMENT</h5>
											<div className="metric-item">
												<span className="metric-label">Click Rate</span>
												<span className="metric-value">
													{formatPercentage(aggregatedMetrics.avgClickRate)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">Viewability</span>
												<span className="metric-value">
													{formatPercentage(aggregatedMetrics.avgViewability)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">Clicks</span>
												<span className="metric-value">
													{formatNumber(aggregatedMetrics.totalClicks)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">Video Completions</span>
												<span className="metric-value">
													{formatNumber(
														aggregatedMetrics.totalVideoCompletions,
													)}
												</span>
											</div>
										</div>

										{/* Revenue Group */}
										<div className="metric-group">
											<h5 className="group-title">REVENUE</h5>
											<div className="metric-item">
												<span className="metric-label">Average eCPM</span>
												<span className="metric-value">
													{formatCurrency(aggregatedMetrics.avgEcpm)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">eCPM Range</span>
												<span className="metric-value">
													{formatCurrency(aggregatedMetrics.ecpmRange.min)} -{" "}
													{formatCurrency(aggregatedMetrics.ecpmRange.max)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">Spend Per User</span>
												<span className="metric-value">
													{formatCurrency(aggregatedMetrics.avgSpendPerUser)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">Advertiser Spend</span>
												<span className="metric-value">
													{formatCurrency(
														aggregatedMetrics.totalAdvertiserSpend,
													)}
												</span>
											</div>
										</div>

										{/* Scale Group */}
										<div className="metric-group">
											<h5 className="group-title">SCALE</h5>
											<div className="metric-item">
												<span className="metric-label">Total Requests</span>
												<span className="metric-value">
													{formatNumber(aggregatedMetrics.totalRequests)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">Matched Count</span>
												<span className="metric-value">
													{formatNumber(aggregatedMetrics.totalMatchedCount)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">Cookies</span>
												<span className="metric-value">
													{formatNumber(aggregatedMetrics.totalCookies)}
												</span>
											</div>
											<div className="metric-item">
												<span className="metric-label">Fills</span>
												<span className="metric-value">
													{formatNumber(aggregatedMetrics.totalFills)}
												</span>
											</div>
										</div>
									</div>
								</div>

								{/* Device Distribution */}
								<div className="device-section">
									<h4 className="section-title">Device Distribution</h4>
									<div className="device-grid">
										<div className="device-item">
											<span className="device-label">Mobile - </span>
											<span className="device-value">
												{formatNumber(aggregatedMetrics.totalMobileDevices)}
											</span>
										</div>
										<div className="device-item">
											<span className="device-label">Connected TV - </span>
											<span className="device-value">
												{formatNumber(
													aggregatedMetrics.totalConnectedTvDevices,
												)}
											</span>
										</div>
										<div className="device-item">
											<span className="device-label">Tablet - </span>
											<span className="device-value">
												{formatNumber(aggregatedMetrics.totalTabletDevices)}
											</span>
										</div>
										<div className="device-item">
											<span className="device-label">Desktop - </span>
											<span className="device-value">
												{formatNumber(aggregatedMetrics.totalDesktopDevices)}
											</span>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Segments Tab Content */}
						{modalActiveTab === "segments" && !error && (
							<div className="tab-content">
								{sortedSegments.length > 0 ? (
									<div className="detail-group">
										<h4>Segments in Set ({sortedSegments.length})</h4>
										<div className="modal-table-container">
											<table className="modal-table segments-table">
												<thead>
													<tr>
														<th
															onClick={() => handleSort("segmentName")}
															style={{ cursor: "pointer" }}
														>
															Segment Name
															<SortIcon
																column="segmentName"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("category")}
															style={{ cursor: "pointer" }}
														>
															Category
															<SortIcon
																column="category"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("uniqueUsers")}
															style={{ cursor: "pointer" }}
														>
															Reach
															<SortIcon
																column="uniqueUsers"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() =>
																handleSort("uniqueUsersViaConnectedTv")
															}
															style={{ cursor: "pointer" }}
														>
															CTV Users
															<SortIcon
																column="uniqueUsersViaConnectedTv"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("mobileDevices")}
															style={{ cursor: "pointer" }}
														>
															Mobile Devices
															<SortIcon
																column="mobileDevices"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("connectedTvDevices")}
															style={{ cursor: "pointer" }}
														>
															CTV Devices
															<SortIcon
																column="connectedTvDevices"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("tabletDevices")}
															style={{ cursor: "pointer" }}
														>
															Tablet Devices
															<SortIcon
																column="tabletDevices"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("weightedImpressions")}
															style={{ cursor: "pointer" }}
														>
															Impressions
															<SortIcon
																column="weightedImpressions"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("requests")}
															style={{ cursor: "pointer" }}
														>
															Requests
															<SortIcon
																column="requests"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() =>
																handleSort("activeUsersWithImpressions")
															}
															style={{ cursor: "pointer" }}
														>
															Users with Impressions
															<SortIcon
																column="activeUsersWithImpressions"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("spendInUsd")}
															style={{ cursor: "pointer" }}
														>
															Spend ($)
															<SortIcon
																column="spendInUsd"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("avgEcpm")}
															style={{ cursor: "pointer" }}
														>
															Avg CPM ($)
															<SortIcon
																column="avgEcpm"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
														<th
															onClick={() => handleSort("compositeScore")}
															style={{ cursor: "pointer" }}
														>
															Score
															<SortIcon
																column="compositeScore"
																currentSortColumn={sortBy}
																sortDirection={sortOrder}
															/>
														</th>
													</tr>
												</thead>
												<tbody>
													{sortedSegments.map((segment, index) => {
														const segmentType = getSegmentTypeHelper(
															segment,
															categoryDataMap,
														);
														const segmentId = segment.segmentVector?.segmentId;

														return (
															<tr
																key={segmentId || index}
																className="modal-table-row"
															>
																<td className="segment-name-cell">
																	<span
																		className="segment-name truncated-text"
																		title={
																			segment.segmentVector?.fullName ||
																			"Unknown Segment"
																		}
																	>
																		{segment.segmentVector?.segmentName ||
																			"Unknown Segment"}
																	</span>
																</td>
																<td>
																	<span
																		className={`category-badge ${segmentType.toLowerCase()}`}
																	>
																		{segmentType}
																	</span>
																</td>
																<td>
																	{formatNumber(
																		segment.segmentMetrics?.calculatedReach ||
																			0,
																	)}
																</td>
																<td>
																	{formatNumber(
																		segment.segmentMetrics
																			?.uniqueUsersViaConnectedTv || 0,
																	)}
																</td>
																<td>
																	{formatNumber(
																		segment.segmentMetrics?.mobileDevices || 0,
																	)}
																</td>
																<td>
																	{formatNumber(
																		segment.segmentMetrics
																			?.connectedTvDevices || 0,
																	)}
																</td>
																<td>
																	{formatNumber(
																		segment.segmentMetrics?.tabletDevices || 0,
																	)}
																</td>
																<td>
																	{formatNumber(
																		segment.segmentMetrics
																			?.weightedImpressions || 0,
																	)}
																</td>
																<td>
																	{formatNumber(
																		segment.segmentMetrics?.requests || 0,
																	)}
																</td>
																<td>
																	{formatNumber(
																		segment.segmentMetrics
																			?.uniqueUsersWithImpressions || 0,
																	)}
																</td>
																<td>
																	$
																	{formatNumber(
																		segment.segmentMetrics
																			?.weightedSpendInUsd || 0,
																	)}
																</td>
																<td>
																	$
																	{(
																		segment.segmentMetrics?.avgCpm || 0
																	).toFixed(2)}
																</td>
																<td>
																	<div className="score-display">
																		<span
																			className={`score-value ${getCompositeScoreClass(segment.compositeScore)}`}
																		>
																			{segment.compositeScore
																				? segment.compositeScore.toFixed(1)
																				: "N/A"}
																		</span>
																	</div>
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</div>
									</div>
								) : (
									<div className="no-segments-message">
										No segments found for this set.
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
			{/* Enhanced Styles with ORF Section */}
			<style jsx>{`
                /* Set Modal Styles - Using Provided Design */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                }

                .set-modal-enhanced,
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    width: 900px;
                    height: 650px;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
                    overflow: hidden;
                }

                .modal-header {
                    background: #f8f9fa;
                    border-bottom: 1px solid #e9ecef;
                    padding: 20px 24px;
                }

                .header-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }

                .modal-main-title {
                    margin: 0 0 6px 0;
                    font-size: 20px;
                    font-weight: 600;
                    color: #212529;
                    line-height: 1.3;
                }

                .modal-close {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    color: #6c757d;
                    font-size: 20px;
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }

                .modal-close:hover {
                    background: #e9ecef;
                    color: #495057;
                }

                .modal-tabs {
                    display: flex;
                    background: #f8f9fa;
                    border-bottom: 1px solid #e9ecef;
                }

                .modal-tab {
                    flex: 1;
                    padding: 14px 16px;
                    border: none;
                    background: transparent;
                    color: #6c757d;
                    font-weight: 500;
                    font-size: 14px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                    border-bottom: 2px solid transparent;
                    position: relative;
                }

                .modal-tab:hover {
                    background: #f0f0f0;
                    color: #495057;
                }

                .modal-tab.active {
                    background: white;
                    color: #495057;
                    border-bottom-color: #495057;
                    font-weight: 600;
                }

                .tab-icon {
                    font-size: 16px;
                }

                .tab-loading {
                    font-size: 12px;
                    margin-left: 4px;
                }

                .modal-body {
                    flex: 1;
                    overflow-y: auto;
                }

                .tab-content {
                    padding: 20px 24px;
                }

                .section-title {
                    margin: 0 0 12px 0;
                    font-size: 15px;
                    font-weight: 600;
                    color: #495057;
                    padding-bottom: 6px;
                    border-bottom: 1px solid #e9ecef;
                }

                .segment-name-cell {
    max-width: 150px;
    position: relative;
}

.truncated-text {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: help;
}

.truncated-text:hover {
    overflow: visible;
    white-space: normal;
    word-wrap: break-word;
    position: absolute;
    background: white;
    border: 1px solid #ccc;
    padding: 4px 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    z-index: 1000;
    max-width: 400px;
    border-radius: 4px;
}

                /* Loading/Error States */
                .loading-section, .error-section, .no-data-section {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 40px 20px;
                    text-align: center;
                }

                .loading-spinner {
                    width: 32px;
                    height: 32px;
                    border: 3px solid #f3f3f3;
                    border-top: 3px solid #007bff;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 16px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .error-icon {
                    font-size: 48px;
                    margin-bottom: 16px;
                }

                .retry-button {
                    background: #007bff;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 6px;
                    cursor: pointer;
                    margin-top: 12px;
                    font-weight: 500;
                    transition: background-color 0.2s;
                }

                .retry-button:hover {
                    background: #0056b3;
                }

                /* ORF Analysis Section Styles */
                .orf-section {
                    margin-bottom: 24px;
                    padding: 16px;
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    border: 1px solid #bae6fd;
                    border-radius: 8px;
                }

                .orf-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                    margin-bottom: 16px;
                }

                .orf-card {
                    background: white;
                    border: 1px solid #e0e7ff;
                    border-radius: 8px;
                    padding: 16px;
                    text-align: center;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    transition: transform 0.2s ease;
                }

                .orf-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
                }

                .orf-card.primary {
                    border-color: #3b82f6;
                    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
                }

                .orf-value {
                    font-size: 22px;
                    font-weight: 700;
                    color: #1e40af;
                    margin-bottom: 4px;
                    line-height: 1.2;
                }

                .orf-label {
                    font-size: 11px;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }

                .orf-description {
                    font-size: 12px;
                    color: #64748b;
                    line-height: 1.3;
                }

                .orf-efficiency {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 12px;
                }

                .efficiency-label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #475569;
                    margin-bottom: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .efficiency-bar-container {
                    position: relative;
                    height: 24px;
                    background: #f1f5f9;
                    border-radius: 12px;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                }

                .efficiency-bar {
                    height: 100%;
                    transition: width 0.5s ease;
                    border-radius: 12px;
                }

                .efficiency-text {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    font-size: 11px;
                    font-weight: 600;
                    color: #1e293b;
                    z-index: 1;
                }

                /* KPI Grid and Performance Section */
                .kpi-section {
                    margin-bottom: 24px;
                }

                .kpi-grid {
                    display: grid;
                    grid-template-columns: repeat(5, 1fr);
                    gap: 12px;
                    margin-bottom: 20px;
                }

                .kpi-card {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 6px;
                    padding: 16px 12px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    min-height: 80px;
                }

                .kpi-value {
                    font-size: 18px;
                    font-weight: 700;
                    color: #212529;
                    margin-bottom: 6px;
                    line-height: 1.2;
                    word-break: break-word;
                }

                .kpi-label {
                    font-size: 11px;
                    color: #6c757d;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    line-height: 1.3;
                    text-align: center;
                }

                /* Score Badges */
                .score-badges {
                    display: flex;
                    gap: 12px;
                    justify-content: center;
                }

                .score-badge {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: 600;
                }

                .score-badge.composite {
                    background: #dbeafe;
                    color: #1e40af;
                }

                .score-badge.search {
                    background: #dcfce7;
                    color: #166534;
                }

                .badge-label {
                    font-size: 12px;
                }

                .badge-value {
                    font-size: 16px;
                }

                /* Performance Metrics - Fixed Alignment */
                .performance-section {
                    margin-bottom: 24px;
                }

                .metrics-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    align-items: stretch;
                }

                .metric-group {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 6px;
                    padding: 14px;
                    display: flex;
                    flex-direction: column;
                    min-height: 200px;
                }

                .group-title {
                    font-size: 13px;
                    font-weight: 600;
                    color: #495057;
                    margin: 0 0 12px 0;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    text-align: center;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #dee2e6;
                }

                .metric-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 0;
                    border-bottom: 1px solid #f1f3f4;
                    font-size: 13px;
                    min-height: 32px;
                }

                .metric-item:last-child {
                    border-bottom: none;
                    margin-top: auto;
                }

                .metric-item .metric-label {
                    color: #6c757d;
                    font-weight: 500;
                    text-transform: uppercase;
                    font-size: 11px;
                    letter-spacing: 0.5px;
                    flex: 1;
                    text-align: left;
                    line-height: 1.4;
                    padding-right: 12px;
                }

                .metric-item .metric-value {
                    color: #212529;
                    font-weight: 400;
                    font-size: 13px;
                    text-align: right;
                    white-space: nowrap;
                    min-width: fit-content;
                }

                /* Device Distribution */
                .device-section {
                    margin-bottom: 24px;
                }

                .device-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px;
                }

                .device-item {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 6px;
                    padding: 12px;
                    text-align: center;
                }

                .device-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #212529;
                    margin-bottom: 2px;
                }

                .device-label {
                    font-size: 11px;
                    color: #6c757d;
                    font-weight: 500;
                    text-transform: uppercase;
                }

                /* API Details Section */
                .api-details-section {
                    margin-bottom: 24px;
                }

                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                }

                .info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    padding: 10px;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 6px;
                }

                .info-label {
                    font-size: 11px;
                    color: #6c757d;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .info-value {
                    font-size: 13px;
                    color: #212529;
                    font-weight: 500;
                    word-break: break-word;
                }

                /* Segments Table */
                .modal-table-container {
                    overflow-x: auto;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    background: white;
                }

                .modal-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                    min-width: 1200px;
                }

                .modal-table th {
                    background: #f8fafc;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 12px 8px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 11px;
                    color: #475569;
                    white-space: nowrap;
                    position: sticky;
                    top: 0;
                    z-index: 1;
                    cursor: pointer;
                }

                .modal-table td {
                    padding: 10px 8px;
                    border-bottom: 1px solid #e2e8f0;
                    vertical-align: top;
                }

                .segment-name-cell {
                    max-width: 200px;
                }

                .segment-name {
                    font-weight: 600;
                    color: #1e293b;
                    word-wrap: break-word;
                }

                .category-badge {
                    background: #dbeafe;
                    color: #1e40af;
                    padding: 2px 6px;
                    border-radius: 12px;
                    font-size: 10px;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .category-badge.oa {
                    background: #dcfce7;
                    color: #166534;
                }

                .category-badge.oaa {
                    background: #fef3c7;
                    color: #92400e;
                }

                .score-display {
                    text-align: center;
                }

                .score-value {
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 600;
                }

                .score-value.excellent {
                    background: #dcfce7;
                    color: #166534;
                }

                .score-value.great {
                    background: #dbeafe;
                    color: #1e40af;
                }

                .score-value.acceptable {
                    background: #fef3c7;
                    color: #92400e;
                }

                .score-value.poor {
                    background: #fed7d7;
                    color: #c53030;
                }

                .score-value.irrelevant {
                    background: #f1f5f9;
                    color: #64748b;
                }

                .no-segments-message {
                    text-align: center;
                    padding: 40px;
                    color: #6b7280;
                }

                .sort-icon {
                    display: inline-flex;
                    align-items: center;
                    margin-left: 4px;
                    opacity: 0.6;
                    transition: opacity 0.2s;
                    flex-shrink: 0;
                }

                .modal-table th:hover .sort-icon {
                    opacity: 1;
                }

                /* Responsive Design */
                @media (max-width: 1200px) {
                    .kpi-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                    
                    .metrics-grid {
                        grid-template-columns: 1fr;
                        gap: 20px;
                    }
                    
                    .device-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .orf-grid {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                }

                @media (max-width: 768px) {
                    .set-modal-enhanced {
                        width: 98vw;
                        height: 95vh;
                    }
                    
                    .kpi-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 12px;
                    }
                    
                    .kpi-card {
                        padding: 16px 12px;
                    }
                    
                    .kpi-value {
                        font-size: 20px;
                    }
                    
                    .score-badges {
                        flex-direction: column;
                        align-items: center;
                    }
                    
                    .device-grid {
                        grid-template-columns: 1fr;
                    }

                    .orf-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
		</>
	);
};
